/**
 * Client-side derivations over the metrics API payload.
 *
 * What is left here is presentation-only: the calendar grid and the streak
 * arithmetic, both computed from the sparse day list the activity endpoint
 * returns. Anything a manager could also ask about the same learner — chapter
 * completion, improvement, what counts as scored or finished — lives on the
 * server so the two views cannot disagree.
 *
 * The rules that could disagree with a manager's view of the same learner
 * (improvement, what counts as scored or finished) deliberately stay on the
 * server. Only presentation-level rollups live here.
 */

/**
 * Per-chapter status and completion percentage.
 *
 * `percent` weights the three criteria by how many items each contributes, so a
 * chapter with 11 lessons and 1 quiz is not reported half done after the quiz.
 */
/*
 * chapterRows() and contentSummary() used to live here. They now live on the
 * server in services/metrics/chapterProgress.service.js, because the manager
 * page needs the same chapter-completion numbers for every member of an org and
 * a second implementation here had already started to disagree — a chapter with
 * no modules read as complete on one side and not the other. The payload carries
 * `status`, `percent` and `content.summary` ready to render.
 */

/* ------------------------------------------------------------------ *
 * activity calendar
 * ------------------------------------------------------------------ */

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Shade band for a day, by minutes in the app.
 * 0 = nothing, then <=15m, <=35m, <=60m, >60m.
 *
 * Minutes rather than module count, because five abandoned case studies in a
 * minute is not a heavier day than one lesson watched properly.
 */
const intensity = (minutes) => {
  if (!minutes) return 0;
  if (minutes <= 15) return 1;
  if (minutes <= 35) return 2;
  if (minutes <= 60) return 3;
  return 4;
};

/**
 * Build 53 columns of 7 days, Sunday-first, ending today — the GitHub layout.
 * The API returns sparse days (no zero rows), so gaps are filled here.
 */
export function buildCalendar(days) {
  const byDate = new Map((days || []).map((d) => [d.date, d]));

  const today = new Date();
  today.setHours(12, 0, 0, 0);

  // Walk back to the Sunday on or before (today - 364) so columns are whole weeks.
  const start = new Date(today);
  start.setDate(start.getDate() - 364);
  start.setDate(start.getDate() - start.getDay());

  const weeks = [];
  const cursor = new Date(start);
  while (cursor <= today) {
    const col = [];
    for (let i = 0; i < 7; i++) {
      const key = dayKey(cursor);
      const hit = byDate.get(key);
      col.push({
        key,
        date: new Date(cursor),
        future: cursor > today,
        minutes: hit ? hit.minutes : 0,
        modules: hit ? hit.attempts : 0,
        lessons: hit && hit.byType ? hit.byType.lesson : 0,
        quizzes: hit && hit.byType ? hit.byType.quiz : 0,
        cases: hit && hit.byType ? hit.byType.negotiation : 0,
        intensity: intensity(hit ? hit.minutes : 0),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(col);
  }
  return weeks;
}

/** Streaks and headline counts for the calendar card. */
export function activitySummary(days) {
  const list = days || [];
  const keys = new Set(list.map((d) => d.date));
  const totalMinutes = list.reduce((s, d) => s + (d.minutes || 0), 0);

  const today = new Date();
  today.setHours(12, 0, 0, 0);

  // Current streak: walk back from today. A day with activity today OR yesterday
  // starts the streak, so checking in the evening does not reset it at midnight.
  let current = 0;
  const probe = new Date(today);
  if (!keys.has(dayKey(probe))) probe.setDate(probe.getDate() - 1);
  while (keys.has(dayKey(probe))) {
    current++;
    probe.setDate(probe.getDate() - 1);
  }

  // Longest streak: sort the active days and count consecutive runs.
  const sorted = [...keys].sort();
  let longest = 0;
  let run = 0;
  let prev = null;
  sorted.forEach((k) => {
    const d = new Date(`${k}T12:00:00`);
    if (prev && (d - prev) / 86400000 === 1) run++;
    else run = 1;
    if (run > longest) longest = run;
    prev = d;
  });

  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() - 30);
  const daysLast30 = list.filter((d) => new Date(`${d.date}T12:00:00`) >= cutoff).length;

  return {
    activeDays: list.length,
    totalMinutes,
    currentStreak: current,
    longestStreak: longest,
    daysLast30,
    avgMinutesPerActiveDay: list.length ? Math.round(totalMinutes / list.length) : 0,
  };
}
